// Application state: database, active clinic, unlocked keys, roles, device.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PowerSyncDatabase } from '@powersync/web';
import { PowerSyncContext } from '@powersync/react';
import { connectIfSignedIn, disconnectAndSignOut, getDb } from '@/lib/db/db';
import { env } from '@/lib/env';
import { ClinicCrypto } from '@/lib/crypto/clinic-crypto';
import { clearClinicKeys, loadClinicKeys } from '@/lib/crypto/keystore';
import type { ClinicRow } from '@/lib/db/schema';
import { rebuildPatientIndex } from '@/features/patients/repo';
import { ensureNumberBlocks } from '@/features/session/numbers';
import { materializeTodaySessions } from '@/features/queue/schedule';
import { getDeviceMode } from './device';

export type Role = 'owner' | 'doctor' | 'nurse' | 'receptionist' | 'cashier' | 'pharmacist' | 'lab_tech' | 'support';

export interface Session {
  mode: 'demo' | 'cloud';
  userId: string;
  clinicId: string;
  deviceId: string | null;
  tokenPrefix: string;
  roles: Role[];
}

interface AppState {
  db: PowerSyncDatabase | null;
  ready: boolean;
  session: Session | null;
  clinic: ClinicRow | null;
  crypto: ClinicCrypto | null;
  setSession: (s: Session | null) => void;
  setCrypto: (c: ClinicCrypto | null) => void;
  hasRole: (...roles: Role[]) => boolean;
}

const Ctx = createContext<AppState | null>(null);
const SESSION_KEY = 'klx.session';

function loadSession(): Session | null {
  try { const raw = localStorage.getItem(SESSION_KEY); return raw ? (JSON.parse(raw) as Session) : null; } catch { return null; }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [db, setDb] = useState<PowerSyncDatabase | null>(null);
  const [session, setSessionState] = useState<Session | null>(loadSession);
  const [crypto, setCrypto] = useState<ClinicCrypto | null>(null);
  const [clinic, setClinic] = useState<ClinicRow | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void getDb().then(async (d) => {
      // Resume sync on reload when a Supabase session is still valid.
      await connectIfSignedIn(d).catch((e) => console.warn('sync not started', e));
      setDb(d);
    });
  }, []);

  const setSession = useCallback((s: Session | null) => {
    setSessionState(s);
    try { s ? localStorage.setItem(SESSION_KEY, JSON.stringify(s)) : localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    if (!s) {
      setCrypto(null); setClinic(null);
      if (session) void clearClinicKeys(session.clinicId).catch(() => undefined);
      if (db) void disconnectAndSignOut(db).catch((e) => console.warn('sign-out cleanup failed', e));
    }
  }, [db, session]);

  // Restore keys from the device keystore and load the clinic row.
  useEffect(() => {
    if (!db) return;
    if (!session) { setReady(true); return; }
    let cancelled = false;
    (async () => {
      const stored = await loadClinicKeys(session.clinicId);
      const cc = ClinicCrypto.fromStored(session.clinicId, stored);
      const row = await db.getOptional<ClinicRow>('select * from clinics where id = ?', [session.clinicId]);
      if (cancelled) return;
      setCrypto(cc);
      setClinic(row);
      setReady(true);
      if (session.mode === 'cloud') void ensureNumberBlocks(db, session.clinicId, session.deviceId).catch(() => undefined);
    })();
    return () => { cancelled = true; };
  }, [db, session]);

  // Rebuild the local decrypted search index whenever encrypted patient rows arrive or change
  // (own writes, sync from other devices, or a re-download after sync rules change).
  useEffect(() => {
    if (!db || !session || !crypto) return;
    const ac = new AbortController();
    let running = false;
    db.watch('select count(*) as n, max(updated_at) as u from patients where clinic_id = ?', [session.clinicId], {
      onResult: () => {
        if (running) return;
        running = true;
        rebuildPatientIndex({ db, crypto, clinicId: session.clinicId, deviceId: session.deviceId, userId: session.userId })
          .catch((e) => console.warn('index rebuild failed', e))
          .finally(() => { running = false; });
      },
    }, { signal: ac.signal });
    return () => ac.abort();
  }, [db, session, crypto]);

  // Today's sessions from the weekly timetable, on load and every 5 minutes (reception and owner devices).
  useEffect(() => {
    if (!db || !session || !crypto) return;
    const mode = getDeviceMode() ?? 'full';
    if (mode !== 'reception' && mode !== 'full') return;
    const run = () => materializeTodaySessions(db, session.clinicId, session.userId, session.deviceId).catch((e) => console.warn('schedule', e));
    void run();
    const id = setInterval(run, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [db, session, crypto]);

  // Keep the clinic row fresh (name changes, settings).
  useEffect(() => {
    if (!db || !session) return;
    const ac = new AbortController();
    db.watch('select * from clinics where id = ?', [session.clinicId], { onResult: (r) => setClinic((r.rows?._array[0] as ClinicRow | undefined) ?? null) }, { signal: ac.signal });
    return () => ac.abort();
  }, [db, session]);

  const hasRole = useCallback((...roles: Role[]) => !!session && roles.some((r) => session.roles.includes(r)), [session]);
  const value = useMemo<AppState>(() => ({ db, ready, session, clinic, crypto, setSession, setCrypto, hasRole }), [db, ready, session, clinic, crypto, setSession, hasRole]);

  if (!db) return null;
  return (
    <PowerSyncContext.Provider value={db}>
      <Ctx.Provider value={value}>{children}</Ctx.Provider>
    </PowerSyncContext.Provider>
  );
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

/** Repository context for an unlocked clinic. Throws if not unlocked; guard routes first. */
export function useClinicCtx() {
  const { db, session, crypto } = useApp();
  if (!db || !session || !crypto) throw new Error('clinic not unlocked');
  return useMemo(() => ({ db, crypto, clinicId: session.clinicId, deviceId: session.deviceId, userId: session.userId, tokenPrefix: session.tokenPrefix }), [db, crypto, session]);
}

export const isCloud = env.cloud;
